import {Input, Radio, Select} from 'antd'
import {baseFieldTypes} from 'apis/base/baseFieldTypes'
import ImageLoader from 'components/ImageLoader'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {ActionModalWrapper} from 'components/form/ActionModalWrapper'
import {createValue} from 'components/form/utils'
import {Formik} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo} from 'react'
import {AdvancedFormFields} from 'views/AppLocals/custom/AdvancedFormGroup'
import CourseCoverField from '../../AppLocals/Course/CourseSet/components/CourseCoverField'
import {createControlledFormField, createNumberControlledInput} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {medal_getMedals_Api} from '../../../apis'
import {bindQueryParams} from '../../AppLocals/functions/routerHelper'
import useAsync from '../../../modules/asyncCache/useAsync'
import {
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noSpecialChars,
    required,
    runRules,
} from '../../../components/form/ValidateForm'
import {dropdownComponents} from "../../AppLocals/custom/ReactSelectComponents";
import {Section} from "components/form/ModernForm";

// ─── Medal select with image thumbnail ───────────────────────────────────────
// Exported so other forms (CourseMedalForm, CurriculumForm) can reuse.

export const MedalSelectField = ({
    name,
    value,
    onChange,
    form,
    disabled = false,
    placeholder,
}) => {
    const t = useTranslate()
    const medals = _.get(form, 'values.medals', [])
    return (
        <Select
            disabled={disabled}
            className="w-full"
            placeholder={placeholder ?? t('click to select')}
            value={value || undefined}
            allowClear
            showSearch
            filterOption={(input, option) =>
                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(v) => onChange(createValue(name, v ?? undefined))}
            suffixIcon={<dropdownComponents.DropdownIndicator/>}
            options={medals.map((m) => ({
                value: m.id,
                searchText: m.title,
                label: (
                    <div className="flex items-center gap-2">
                        <ImageLoader
                            src={m.avatar}
                            style={{width: 28, height: 22, objectFit: 'contain'}}
                        />
                        <span className="text-sm">{m.title}</span>
                    </div>
                ),
            }))}
        />
    )
}

// ─── Tags select ──────────────────────────────────────────────────────────────
// Plain antd Select (mode=tags) — SelectWidget hides value when no options.

const TagsField = ({name, value, onChange}) => {
    const t = useTranslate()
    return (
        <Select
            mode="tags"
            className="w-full"
            placeholder={t('tags')}
            value={value}
            allowClear
            onChange={(v) => onChange(createValue(name, v))}
            suffixIcon={<dropdownComponents.DropdownIndicator/>}
        />
    )
}

// ─── Form schema ──────────────────────────────────────────────────────────────

const formSchema = {
    avatar: {
        children: [
            {
                name: 'avatar',
                // Dùng lại CourseCoverField (field upload ảnh có khung) - ảnh mẫu chứng nhận tỉ lệ 4:3.
                component: ({name, value, form, onChange}) => (
                    <Translate>
                        {(t) => (
                            <CourseCoverField
                                {...{name, value, onChange}}
                                values={_.get(form, 'values')}
                                cropPurpose="certificate"
                                selectedLabel={t('certificate image selected')}
                                note={t('certificate image note')}
                                previewStyle={{height: '22vw', minHeight: 170, maxHeight: 260}}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    title: {
        children: {
            label: 'name of certification',
            isRequired: true,
            name: 'title',
            component: createControlledFormField({
                placeholder: 'name of certification',
                showCount: true,
                maxLength: 180,
            }),
        },
    },
    description: {
        title: 'description',
        children: {
            name: 'description',
            component: createControlledFormField({
                rows: 3,
                showCount: true,
                maxLength: 500,
                placeholder: 'description',
                Component: Input.TextArea,
            }),
        },
    },
    // Level (narrow) + Tags (wide) side by side
    levelAndTags: {
        inline: true,
        className: 'flex gap-3',
        itemClassNames: ['w-2/5', 'flex-1'],
        children: [
            {
                label: 'level',
                description: 'max 100',
                name: 'lvl_number',
                component: createNumberControlledInput({
                    placeholder: '1',
                    inputMode: 'number',
                    min: 1,
                    max: 100,
                }),
            },
            {
                name: 'tags',
                label: 'tags',
                component: TagsField,
            },
        ],
    },
    active: {
        title: 'active',
        children: [
            {
                name: 'active',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Radio.Group
                                name={name}
                                value={value}
                                onChange={onChange}
                                buttonStyle="solid"
                                optionType="button"
                                className="custom-radio-button"
                                options={baseFieldTypes.active.map(deepTranslate(t))}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    medal_1_id: {
        title: 'medal 1',
        children: [{name: 'medal_1_id', component: MedalSelectField}],
    },
    medal_2_id: {
        title: 'medal 2',
        children: [{name: 'medal_2_id', component: MedalSelectField}],
    },
    new_medal_id: {
        title: 'new medal',
        children: [{name: 'new_medal_id', component: MedalSelectField}],
    },
}

// ─── Validation ───────────────────────────────────────────────────────────────

const validate = (t) => (values) => {
    const {title, lvl_number, tags} = values ?? {}
    const errors = {}

    const titleMsg = runRules(title, [
        required(t),
        lengthBetween(2, 180, t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean))
    if (titleMsg) errors.title = titleMsg

    const lvlMsg = runRules(lvl_number, [required(t)].filter(Boolean))
    if (lvlMsg) {
        errors.lvl_number = lvlMsg
    }

    if (Array.isArray(tags) && tags.length > 0) {
        const tagRules = [
            lengthBetween(2, 255, t),
            noSpecialChars(t),
            noLeadingTrailingSpaces(t),
            noDoubleSpaces(t),
        ]
        for (const tag of tags) {
            const msg = runRules(tag, tagRules)
            if (msg) {
                errors.tags = msg
                break
            }
        }
    }

    return errors
}

// ─── Shared form body (Create + Edit reuse) ───────────────────────────────────

const MedalFormBody = ({isSubmitting, submitLabel}) => {
    const t = useTranslate()
    return (
        <ActionModalWrapper
            className="gap-4"
            action={
                <FormActionGroups
                    isSubmitting={isSubmitting}
                    submitProps={{label: submitLabel}}
                />
            }>
            <FieldsFactory formSchema={[formSchema.avatar]}/>
            <Section title={t('basic information')}>
                <FieldsFactory
                    formSchema={[
                        formSchema.title,
                        formSchema.description,
                    ]}
                />
            </Section>
            <Section title={t('medal settings')}>
                <FieldsFactory formSchema={[formSchema.levelAndTags]}/>
                <FieldsFactory formSchema={[formSchema.active]}/>
            </Section>
        </ActionModalWrapper>
    )
}

// ─── Create ───────────────────────────────────────────────────────────────────

export const CreateMedal = ({
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const initialValues = useMemo(
        () => ({
            active: 1,
            owner_id,
            owner_type,
            tags: undefined,
            avatar: undefined,
            title: undefined,
            status: undefined,
            lvl_number: 1,
            description: undefined,
        }),
        [owner_id, owner_type]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {owner_id: _oid, owner_type: _otype, avatar_preview, ...rest} = values ?? {}
                const tags = Array.from(values?.tags ?? [])
                onSubmit(
                    _.omitBy(
                        {...rest, tags: tags.join(','), active: Number(values?.active ?? 0)},
                        _.isUndefined
                    )
                )
            }}>
            <MedalFormBody
                isSubmitting={isSubmitting}
                submitLabel="create"
            />
        </Formik>
    )
}

// ─── Edit ─────────────────────────────────────────────────────────────────────

export const EditMedal = ({
    item,
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const initialValues = useMemo(() => {
        const {tags, avatar} = item ?? {}
        return {
            ...(item ?? {}),
            owner_id,
            owner_type,
            avatar_preview: avatar,
            tags: Array.from(tags ?? []).map((e) => e?.tags),
        }
    }, [item, owner_id, owner_type])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {owner_id: _oid, owner_type: _otype, avatar_preview, ...rest} = changes ?? {}
                const tags = Array.from(values?.tags ?? [])
                onSubmit({...rest, tags: tags.join(',')})
            }}>
            <MedalFormBody
                isSubmitting={isSubmitting}
                submitLabel="save"
            />
        </Formik>
    )
}

// ─── Merge ────────────────────────────────────────────────────────────────────

export const MergeMedal = ({
    type = 'student',
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const {response} = useAsync({
        apiInfo: medal_getMedals_Api,
        query: bindQueryParams([{prop: owner_type}, {id: owner_id}]),
    })

    const medals = _.get(response, 'data.data', [])

    const validateMerge = useCallback(
        (values) => {
            const {medal_1_id, medal_2_id, new_medal_id} = values ?? {}
            const errors = {}
            if (_.isEmpty(medal_1_id)) errors.medal_1_id = t('required field')
            if (_.isEmpty(medal_2_id)) errors.medal_2_id = t('required field')
            if (_.isEmpty(new_medal_id)) errors.new_medal_id = t('required field')
            return errors
        },
        [t]
    )

    const initialValues = useMemo(
        () => ({
            owner_id,
            owner_type,
            medal_1_id: undefined,
            medal_2_id: undefined,
            new_medal_id: undefined,
            medals,
        }),
        [medals, owner_id, owner_type]
    )

    if (!medals.length) return null

    return (
        <Formik
            validate={validateMerge}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => onSubmit(values)}>
            <ActionModalWrapper
                className="gap-4"
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'merge'}}
                    />
                }>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex flex-col gap-1">
                    <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                        {t('notes')}
                    </span>
                    <span className="text-xs text-color-300 leading-relaxed">
                        {t('Students who have obtained Certificate 1 (from a different course) and Certificate 2 (from this course) will be awarded an additional certificate, selected from the options below.')}
                    </span>
                </div>
                <FieldsFactory
                    formSchema={[
                        formSchema.medal_1_id,
                        formSchema.medal_2_id,
                        formSchema.new_medal_id,
                    ]}
                />
            </ActionModalWrapper>
        </Formik>
    )
}
